import { useState } from 'react';
import { Box, Typography, Button, Stack, IconButton, TextField, InputAdornment, MenuItem, Menu, Chip, Avatar, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Search, Edit, Trash2, Mail, Phone, Building2, Tag, UserCheck, FilterX, Users, UserPlus } from 'lucide-react';
import { EMPLOYMENT_STATUS_OPTIONS } from '@/constants/staff';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import { useStaffList, useDepartments, useDesignations, useDeleteStaff, useUpdateStaffStatus, useCreateDepartment } from '@/hooks/useStaffManagement';
import { PageHeader } from '@/components/navigation/PageHeader';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { DepartmentModal } from './components/DepartmentModal';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { StatusBadge } from '@/components/feedback/StatusBadge';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';
import toast from 'react-hot-toast';


export function StaffListPage() {
  usePageMeta('Staff Directory | Staff Management', [
    { label: 'Staff Management', path: '/staff-management' },
    { label: 'Staff Directory' },
  ]);

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedDesig, setSelectedDesig] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [deleteStaff, setDeleteStaff] = useState(null);
  const [deptModalOpen, setDeptModalOpen] = useState(false);

  const queryParams = {
    search: search || undefined,
    department_id: selectedDept || undefined,
    designation_id: selectedDesig || undefined,
    employment_status: selectedStatus || undefined,
  };

  const { data: staffList = [], isLoading, error, refetch } = useStaffList(queryParams);
  const { data: departments = [], isLoading: loadingDepts, error: errorDepts, refetch: refetchDepts } = useDepartments();
  const { data: designations = [], isLoading: loadingDesigs } = useDesignations();
  const deleteMutation = useDeleteStaff();
  const statusMutation = useUpdateStaffStatus();
  const createDeptMutation = useCreateDepartment();

  const { can } = usePermission();
  const canCreate = can(PERMISSIONS.STAFF_MANAGE_CREATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canUpdate = can(PERMISSIONS.STAFF_MANAGE_UPDATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canDelete = can(PERMISSIONS.STAFF_MANAGE_DELETE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);

  const deptMap = (departments || []).reduce((acc, d) => ({ ...acc, [d.id]: d.name }), {});
  const desigMap = (designations || []).reduce((acc, d) => ({ ...acc, [d.id]: d.name }), {});

  const activeStaffCount = (staffList || []).filter(
    (s) => !s.employment_status || !s.status || (s.employment_status || s.status).toUpperCase() === 'ACTIVE'
  ).length;

  const filteredDesignationOptions = selectedDept
    ? (designations || []).filter((d) => d.department_id === selectedDept || d.departmentId === selectedDept)
    : designations || [];

  const handleDeleteConfirm = async () => {
    if (!deleteStaff) return;
    try {
      await deleteMutation.mutateAsync(deleteStaff.id);
      toast.success('Staff member removed successfully');
      setDeleteStaff(null);
    } catch (err) {
      toast.error(err.message || 'Failed to remove staff member');
    }
  };

  const handleCreateDepartment = async (payload) => {
    try {
      await createDeptMutation.mutateAsync(payload);
      toast.success('Department created successfully');
      setDeptModalOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create department');
    }
  };

  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [targetStaff, setTargetStaff] = useState(null);

  const handleOpenStatusMenu = (event, row) => {
    event.stopPropagation();
    setStatusMenuAnchor(event.currentTarget);
    setTargetStaff(row);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setTargetStaff(null);
  };

  const handleSelectStatus = async (newStatus) => {
    if (!targetStaff) return;
    try {
      await statusMutation.mutateAsync({ id: targetStaff.id, employmentStatus: newStatus });
      toast.success(`Staff status updated to ${newStatus}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update staff status');
    } finally {
      handleCloseStatusMenu();
    }
  };

  const hasFilters = Boolean(search || selectedDept || selectedDesig || selectedStatus);

  const handleResetFilters = () => {
    setSearch('');
    setSelectedDept('');
    setSelectedDesig('');
    setSelectedStatus('');
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Staff Member',
      flex: 1.2,
      minWidth: 220,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const firstName = params.row.first_name || params.row.firstName || '';
        const lastName = params.row.last_name || params.row.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || params.row.name || 'Unnamed';
        const empId = params.row.employee_code || params.row.employee_id || params.row.employeeId || params.row.id?.slice(0, 6);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={params.row.photo_url || params.row.photoUrl || undefined}
              sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontWeight: 600, fontSize: 14 }}
            >
              {fullName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                {fullName}
              </Typography>
              <Stack direction="row" spacing={0.5} mt={0.5}>
                <Chip
                  label={`EMP: ${empId}`}
                  size="small"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover' }}
                />
                {(params.row.role || params.row.employee_type) && (
                  <Chip
                    label={
                      params.row.role?.description ||
                      (params.row.role?.name
                        ? params.row.role.name.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
                        : params.row.employee_type)
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                  />
                )}
              </Stack>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'department_id',
      headerName: 'Department',
      flex: 1,
      minWidth: 160,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const deptId = params.row.department_id || params.row.departmentId || params.row.department?.id;
        const deptName = params.row.department?.name || deptMap[deptId] || 'Unassigned';
        return (
          <Chip
            icon={<Building2 size={13} />}
            label={deptName}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, borderColor: 'divider' }}
          />
        );
      },
    },
    {
      field: 'designation_id',
      headerName: 'Designation',
      flex: 1,
      minWidth: 160,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const desigId = params.row.designation_id || params.row.designationId || params.row.designation?.id;
        const desigName = params.row.designation?.name || desigMap[desigId] || 'Unassigned';
        return (
          <Chip
            icon={<Tag size={13} />}
            label={desigName}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, borderColor: 'divider' }}
          />
        );
      },
    },
    {
      field: 'contact',
      headerName: 'Contact Info',
      flex: 1.2,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const email = params.row.email || 'No email';
        const phone = params.row.phone || params.row.mobile || 'No mobile';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Typography
              variant="caption"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', lineHeight: 1.4 }}
            >
              <Mail size={12} /> {email}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mt: 0.2, lineHeight: 1.4 }}
            >
              <Phone size={12} /> {phone}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'employment_status',
      headerName: 'Status',
      width: 130,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const status = (params.value || params.row.status || 'ACTIVE').toLowerCase();
        return (
          <Box
            onClick={(e) => canUpdate && handleOpenStatusMenu(e, params.row)}
            sx={{ cursor: canUpdate ? 'pointer' : 'default' }}
          >
            <StatusBadge status={status} />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          {canUpdate && (
            <Tooltip title="Change Employment Status">
              <IconButton
                size="small"
                onClick={(e) => handleOpenStatusMenu(e, params.row)}
                sx={{ color: 'primary.main' }}
              >
                <UserCheck size={16} />
              </IconButton>
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title="Edit Staff Member">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(PATHS.STAFF_MANAGEMENT_STAFF_EDIT.replace(':id', params.row.id));
                }}
                sx={{ color: 'primary.main' }}
              >
                <Edit size={16} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Remove Staff Member">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteStaff(params.row);
                }}
                sx={{ color: 'error.main' }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (error || errorDepts) {
    return (
      <Box>
        <ErrorState error={error || errorDepts} onRetry={error ? refetch : refetchDepts} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Staff Directory"
        subtitle="Manage staff members, departments, designations, and workforce overview"
        actions={
          canCreate && (
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={() => setDeptModalOpen(true)}
                sx={{ bgcolor: 'white' }}
              >
                New Department
              </Button>
              <Button
                variant="contained"
                startIcon={<UserPlus size={16} />}
                onClick={() => navigate(PATHS.STAFF_MANAGEMENT_STAFF_ADD)}
                sx={{ fontWeight: 600 }}
              >
                Enroll Staff
              </Button>
            </Stack>
          )
        }
      />

      {/* ── Overview Stat Cards ── */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }}>
        <StatCard
          title="Departments"
          value={loadingDepts ? '-' : (departments || []).length}
          icon={Building2}
          color="primary"
          onClick={() => navigate(PATHS.STAFF_MANAGEMENT_DEPARTMENTS)}
        />
        <StatCard
          title="Designations"
          value={loadingDesigs ? '-' : (designations || []).length}
          icon={Tag}
          color="secondary"
          onClick={() => navigate(PATHS.STAFF_MANAGEMENT_DESIGNATIONS)}
        />
        <StatCard
          title="Directory"
          value={isLoading ? '-' : (staffList || []).length}
          icon={Users}
          color="info"
        />
        <StatCard
          title="Active Staff"
          value={isLoading ? '-' : activeStaffCount}
          icon={UserCheck}
          color="success"
        />
      </DashboardGrid>



      {/* Filter and Search Bar */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            select
            size="small"
            label="Department"
            value={selectedDept}
            onChange={(e) => {
              setSelectedDept(e.target.value);
              setSelectedDesig('');
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Departments</MenuItem>
            {(departments || []).map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Designation"
            value={selectedDesig}
            onChange={(e) => setSelectedDesig(e.target.value)}
            disabled={!selectedDept && filteredDesignationOptions.length === 0}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Designations</MenuItem>
            {filteredDesignationOptions.map((desig) => (
              <MenuItem key={desig.id} value={desig.id}>
                {desig.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="ON_LEAVE">On Leave</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
            <MenuItem value="RESIGNED">Resigned</MenuItem>
            <MenuItem value="TERMINATED">Terminated</MenuItem>
          </TextField>

          {hasFilters && (
            <Button
              variant="outlined"
              startIcon={<FilterX size={18} />}
              onClick={handleResetFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          autoHeight
          rows={staffList || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id || Math.random().toString()}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          onRowClick={(params) =>
            navigate(PATHS.STAFF_MANAGEMENT_STAFF_PROFILE.replace(':id', params.id))
          }
          sx={{
            border: 0,
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Box>

      {/* Status Selection Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
      >
        {EMPLOYMENT_STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={
              (targetStaff?.employment_status || targetStaff?.status || 'ACTIVE').toUpperCase() === opt.value
            }
            onClick={() => handleSelectStatus(opt.value)}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deleteStaff}
        onClose={() => setDeleteStaff(null)}
        onConfirm={handleDeleteConfirm}
        title="Remove Staff Member"
        message={`Are you sure you want to delete staff member "${deleteStaff?.first_name || ''} ${deleteStaff?.last_name || ''}"? This will deactivate their access.`}
        confirmText="Remove"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />

      {/* Department Modal */}
      <DepartmentModal
        open={deptModalOpen}
        onClose={() => setDeptModalOpen(false)}
        onSubmit={handleCreateDepartment}
        loading={createDeptMutation.isPending}
      />
    </Box>
  );
}
